/**
 * The display (non-input) directive renderers — `card`, `gallery`, `modal`,
 * `toast`. They render read-only or hand off to a surface callback; none park the
 * run. Kept together (small, no local form state) but each is a distinct
 * component per `kind` (OCP). The Artifacts lightbox and the builder/billing
 * modals they open are wired by the surface shell (next task).
 */
import type { UiDirective } from '@4f/shared';
import { Button, MediaTile, useT } from '@4f/ui';
import { For, type JSX, Show } from 'solid-js';
import type { GenerativeUiHandlers } from '../GenerativeUi.tsx';

type CardDirective = Extract<UiDirective, { kind: 'card' }>;
type GalleryDirectiveT = Extract<UiDirective, { kind: 'gallery' }>;
type ModalDirectiveT = Extract<UiDirective, { kind: 'modal' }>;
type ToastDirectiveT = Extract<UiDirective, { kind: 'toast' }>;

/** A preview/confirm card — its actions echo an `action` string back to the run. */
export function CardRenderer(props: {
  readonly directive: CardDirective;
  readonly handlers: GenerativeUiHandlers;
}): JSX.Element {
  return (
    <div class="f4-genui-card">
      <h3 class="f4-genui__title">{props.directive.title}</h3>
      <Show when={props.directive.body}>
        {(body) => <p class="f4-genui-card__body">{body()}</p>}
      </Show>
      <Show when={props.directive.actions.length > 0}>
        <div class="f4-genui-card__actions">
          <For each={props.directive.actions}>
            {(action) => (
              <Button
                type="button"
                variant={action.variant}
                disabled={props.handlers.disabled}
                onClick={() => props.handlers.onSubmit(action.action)}
              >
                {action.label}
              </Button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

/** A grid of her assets — a tile (or the button) opens the shared Artifacts lightbox. */
export function GalleryDirective(props: {
  readonly directive: GalleryDirectiveT;
  readonly handlers: GenerativeUiHandlers;
}): JSX.Element {
  const { t } = useT();
  const open = (index: number): void =>
    props.handlers.onOpenGallery?.(props.directive.assetIds, index);

  return (
    <div class="f4-genui-gallery">
      <div class="f4-genui-gallery__grid">
        <For each={props.directive.assetIds}>
          {(assetId, index) => {
            // The directive carries opaque asset IDS, not URLs. Without resolving
            // them the tile renders `<img src={undefined}>` — a grid of broken
            // images whose only accessible name is a raw UUID. Resolve through the
            // injected cache; while it is still unresolved show the shimmer rather
            // than a broken image, and label every tile with human copy.
            const src = (): string | undefined => props.handlers.assetUrl?.(assetId);
            const alt = (): string => t('surface.galleryItem', { index: index() + 1 });
            return (
              <MediaTile
                src={src()}
                loading={src() === undefined}
                alt={alt()}
                onSelect={() => open(index())}
              />
            );
          }}
        </For>
      </div>
      <Show when={props.handlers.onOpenGallery}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => open(props.directive.startIndex)}
        >
          {t('surface.viewGallery')}
        </Button>
      </Show>
    </div>
  );
}

/** A prompt to open a modal surface (settings/builder/billing/referral). */
export function ModalDirective(props: {
  readonly directive: ModalDirectiveT;
  readonly handlers: GenerativeUiHandlers;
}): JSX.Element {
  const { t } = useT();
  return (
    <Show when={props.handlers.onOpenSurface}>
      <Button
        type="button"
        variant="primary"
        onClick={() => props.handlers.onOpenSurface?.(props.directive.surface)}
      >
        {props.directive.title ?? t('surface.openSurface', { surface: props.directive.surface })}
      </Button>
    </Show>
  );
}

/** A transient inline notice — `assertive` for errors, `polite` otherwise. */
export function ToastDirective(props: { readonly directive: ToastDirectiveT }): JSX.Element {
  return (
    <p
      class={`f4-genui-toast f4-genui-toast--${props.directive.level}`}
      role={props.directive.level === 'error' ? 'alert' : 'status'}
    >
      {props.directive.message}
    </p>
  );
}
